// ── Team Selection Menu ── CS:GO-style team picker overlay
// Shows on first join and toggleable via M key during play.

const T_COLOR = '#e8b849';
const CT_COLOR = '#5b9bd5';
const BG_DARK = 'rgba(0,0,0,0.88)';
const FONT = '"Courier New", monospace';

export type TeamChoice = 'T' | 'CT' | 'auto' | 'spectator';

export interface TeamStat {
  players: number;
  avgRank?: number | string | null;
}

export interface TeamStats {
  t: TeamStat;
  ct: TeamStat;
}

export class TeamSelectMenu {
  private overlay: HTMLElement;
  private onSelect: (team: TeamChoice) => void;
  private visible = false;
  private autoTimer: ReturnType<typeof setTimeout> | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private countdownSeconds = 15;

  // Elements updated dynamically
  private tCountEl: HTMLElement | null = null;
  private ctCountEl: HTMLElement | null = null;
  private tStatsEl: HTMLElement | null = null;
  private ctStatsEl: HTMLElement | null = null;
  private timerEl: HTMLElement | null = null;
  private notificationEl: HTMLElement | null = null;
  private notificationTimer: ReturnType<typeof setTimeout> | null = null;

  private boundKeyHandler = (e: KeyboardEvent) => this.handleKeyDown(e);

  constructor(onSelect: (team: TeamChoice) => void) {
    this.onSelect = onSelect;
    this.overlay = this.createOverlay();
    document.body.appendChild(this.overlay);
    document.addEventListener('keydown', this.boundKeyHandler);
  }

  show(autoTimeoutSeconds = 15): void {
    if (this.visible) return;
    this.visible = true;
    this.overlay.style.display = 'flex';
    this.countdownSeconds = autoTimeoutSeconds;
    this.updateTimerDisplay();
    this.startCountdown();
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.overlay.style.display = 'none';
    this.stopCountdown();
  }

  isOpen(): boolean {
    return this.visible;
  }

  updateTeamCounts(tCount: number, ctCount: number, maxPerTeam: number): void {
    if (this.tCountEl) this.tCountEl.textContent = `${tCount}/${maxPerTeam}`;
    if (this.ctCountEl) this.ctCountEl.textContent = `${ctCount}/${maxPerTeam}`;
  }

  setTeamStats(stats: TeamStats): void {
    this.renderTeamStats(this.tStatsEl, stats.t);
    this.renderTeamStats(this.ctStatsEl, stats.ct);
  }

  /** Show a brief notification message (e.g. "Team is full"). */
  showNotification(message: string): void {
    if (!this.notificationEl) return;
    this.notificationEl.textContent = message;
    this.notificationEl.style.opacity = '1';
    if (this.notificationTimer) clearTimeout(this.notificationTimer);
    this.notificationTimer = setTimeout(() => {
      if (this.notificationEl) this.notificationEl.style.opacity = '0';
    }, 2000);
  }

  dispose(): void {
    document.removeEventListener('keydown', this.boundKeyHandler);
    this.stopCountdown();
    if (this.notificationTimer) clearTimeout(this.notificationTimer);
    this.overlay.remove();
  }

  private select(team: TeamChoice): void {
    this.onSelect(team);
    this.hide();
  }

  private startCountdown(): void {
    this.stopCountdown();
    this.countdownInterval = setInterval(() => {
      this.countdownSeconds--;
      this.updateTimerDisplay();
      if (this.countdownSeconds <= 0) {
        this.select('auto');
      }
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    if (this.autoTimer) {
      clearTimeout(this.autoTimer);
      this.autoTimer = null;
    }
  }

  private updateTimerDisplay(): void {
    if (this.timerEl) {
      this.timerEl.textContent = `Auto-assign in: ${this.countdownSeconds}s`;
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.visible) return;

    switch (e.key) {
      case '1':
        e.preventDefault();
        e.stopPropagation();
        this.select('T');
        break;
      case '2':
        e.preventDefault();
        e.stopPropagation();
        this.select('CT');
        break;
      case '3':
        e.preventDefault();
        e.stopPropagation();
        this.select('auto');
        break;
      case '4':
        e.preventDefault();
        e.stopPropagation();
        this.select('spectator');
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        this.select('auto');
        break;
    }
  }

  private createOverlay(): HTMLElement {
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: BG_DARK,
      zIndex: '55',
      display: 'none',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: FONT,
      color: '#ffffff',
      userSelect: 'none',
    });

    // Title
    const title = document.createElement('div');
    Object.assign(title.style, {
      fontSize: '32px',
      fontWeight: 'bold',
      letterSpacing: '6px',
      textTransform: 'uppercase',
      marginBottom: '8px',
      color: '#e94560',
    });
    title.textContent = 'SELECT TEAM';
    overlay.appendChild(title);

    // Subtitle
    const subtitle = document.createElement('div');
    Object.assign(subtitle.style, {
      fontSize: '13px',
      color: '#666',
      letterSpacing: '2px',
      marginBottom: '30px',
    });
    subtitle.textContent = 'CHOOSE YOUR SIDE';
    overlay.appendChild(subtitle);

    // Team panels container
    const panelsContainer = document.createElement('div');
    Object.assign(panelsContainer.style, {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '24px',
      marginBottom: '24px',
    });

    // T Panel
    const tPanel = this.createTeamPanel(
      'TERRORISTS',
      T_COLOR,
      '1',
      () => this.select('T'),
    );
    this.tCountEl = tPanel.querySelector('[data-count]') as HTMLElement;
    this.tStatsEl = tPanel.querySelector('[data-team-stats]') as HTMLElement;
    panelsContainer.appendChild(tPanel);

    // CT Panel
    const ctPanel = this.createTeamPanel(
      'COUNTER-TERRORISTS',
      CT_COLOR,
      '2',
      () => this.select('CT'),
    );
    this.ctCountEl = ctPanel.querySelector('[data-count]') as HTMLElement;
    this.ctStatsEl = ctPanel.querySelector('[data-team-stats]') as HTMLElement;
    panelsContainer.appendChild(ctPanel);

    overlay.appendChild(panelsContainer);

    // Auto-Select button
    const autoBtn = document.createElement('div');
    Object.assign(autoBtn.style, {
      padding: '12px 40px',
      fontSize: '15px',
      fontWeight: 'bold',
      letterSpacing: '3px',
      textTransform: 'uppercase',
      cursor: 'pointer',
      border: '2px solid #555',
      borderRadius: '4px',
      background: 'rgba(255,255,255,0.05)',
      color: '#aaa',
      transition: 'all 0.15s',
      marginBottom: '12px',
      fontFamily: FONT,
      textAlign: 'center',
    });
    autoBtn.innerHTML =
      '<span style="color:#666;font-size:11px;margin-right:8px;">[3]</span>AUTO-SELECT';
    autoBtn.addEventListener('mouseenter', () => {
      autoBtn.style.borderColor = '#888';
      autoBtn.style.color = '#fff';
      autoBtn.style.background = 'rgba(255,255,255,0.1)';
    });
    autoBtn.addEventListener('mouseleave', () => {
      autoBtn.style.borderColor = '#555';
      autoBtn.style.color = '#aaa';
      autoBtn.style.background = 'rgba(255,255,255,0.05)';
    });
    autoBtn.addEventListener('click', () => this.select('auto'));
    overlay.appendChild(autoBtn);

    // Spectator option
    const specBtn = document.createElement('div');
    Object.assign(specBtn.style, {
      padding: '8px 30px',
      fontSize: '12px',
      letterSpacing: '2px',
      cursor: 'pointer',
      color: '#555',
      transition: 'color 0.15s',
      fontFamily: FONT,
      textAlign: 'center',
    });
    specBtn.innerHTML =
      '<span style="color:#444;font-size:10px;margin-right:6px;">[4]</span>SPECTATOR';
    specBtn.addEventListener('mouseenter', () => {
      specBtn.style.color = '#888';
    });
    specBtn.addEventListener('mouseleave', () => {
      specBtn.style.color = '#555';
    });
    specBtn.addEventListener('click', () => this.select('spectator'));
    overlay.appendChild(specBtn);

    // Countdown timer (top-right)
    const timer = document.createElement('div');
    Object.assign(timer.style, {
      position: 'absolute',
      top: '20px',
      right: '30px',
      fontSize: '16px',
      fontWeight: 'bold',
      color: '#e94560',
      letterSpacing: '1px',
      fontFamily: FONT,
    });
    timer.textContent = 'Auto-assign in: 15s';
    this.timerEl = timer;
    overlay.appendChild(timer);

    // Notification area (center, above panels)
    const notification = document.createElement('div');
    Object.assign(notification.style, {
      position: 'absolute',
      top: '80px',
      left: '50%',
      transform: 'translateX(-50%)',
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#ff4444',
      letterSpacing: '2px',
      fontFamily: FONT,
      textTransform: 'uppercase',
      opacity: '0',
      transition: 'opacity 0.3s',
      textShadow: '0 2px 8px rgba(0,0,0,0.8)',
      pointerEvents: 'none',
    });
    this.notificationEl = notification;
    overlay.appendChild(notification);

    // Key hint at bottom
    const hint = document.createElement('div');
    Object.assign(hint.style, {
      position: 'absolute',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      fontSize: '11px',
      color: '#444',
      letterSpacing: '1px',
      fontFamily: FONT,
    });
    hint.textContent = '[M] Reopen this menu during play  |  [ESC] Auto-assign';
    overlay.appendChild(hint);

    return overlay;
  }

  private createTeamPanel(
    teamName: string,
    accentColor: string,
    keyHint: string,
    onClick: () => void,
  ): HTMLElement {
    const panel = document.createElement('div');
    Object.assign(panel.style, {
      width: 'min(260px, 80vw)',
      padding: '30px 24px',
      border: `2px solid ${accentColor}40`,
      borderRadius: '6px',
      background: 'rgba(255,255,255,0.03)',
      cursor: 'pointer',
      transition: 'all 0.2s',
      textAlign: 'center',
      fontFamily: FONT,
    });

    // Team name
    const name = document.createElement('div');
    Object.assign(name.style, {
      fontSize: '18px',
      fontWeight: 'bold',
      color: accentColor,
      letterSpacing: '3px',
      marginBottom: '16px',
      textTransform: 'uppercase',
    });
    name.textContent = teamName;
    panel.appendChild(name);

    // Player count
    const count = document.createElement('div');
    count.setAttribute('data-count', '1');
    Object.assign(count.style, {
      fontSize: '36px',
      fontWeight: 'bold',
      color: '#ffffff',
      marginBottom: '16px',
      textShadow: `0 0 15px ${accentColor}40`,
    });
    count.textContent = '0/5';
    panel.appendChild(count);

    // Team stats
    const stats = document.createElement('div');
    stats.setAttribute('data-team-stats', '1');
    Object.assign(stats.style, {
      minHeight: '34px',
      marginBottom: '16px',
      fontSize: '11px',
      lineHeight: '1.45',
      color: '#9a9a9a',
      letterSpacing: '1px',
      textTransform: 'uppercase',
      whiteSpace: 'pre-line',
    });
    stats.textContent = 'Players: 0';
    panel.appendChild(stats);

    // Key hint
    const key = document.createElement('div');
    Object.assign(key.style, {
      fontSize: '14px',
      color: '#666',
      border: '1px solid #444',
      borderRadius: '3px',
      padding: '4px 12px',
      display: 'inline-block',
    });
    key.textContent = `[${keyHint}]`;
    panel.appendChild(key);

    // Hover / active (touch) effects — :active mirrors :hover so taps get feedback
    const applyActive = () => {
      panel.style.borderColor = accentColor;
      panel.style.background = `${accentColor}15`;
      panel.style.transform = 'scale(1.02)';
    };
    const clearActive = () => {
      panel.style.borderColor = `${accentColor}40`;
      panel.style.background = 'rgba(255,255,255,0.03)';
      panel.style.transform = 'scale(1)';
    };
    panel.addEventListener('mouseenter', applyActive);
    panel.addEventListener('mouseleave', clearActive);
    panel.addEventListener('touchstart', applyActive, { passive: true });
    panel.addEventListener('touchend', clearActive);
    panel.addEventListener('touchcancel', clearActive);

    panel.addEventListener('click', onClick);

    return panel;
  }

  private renderTeamStats(el: HTMLElement | null, stat: TeamStat): void {
    if (!el) return;
    el.textContent = stat.avgRank !== undefined && stat.avgRank !== null
      ? `Players: ${stat.players}\nAvg rank: ${stat.avgRank}`
      : `Players: ${stat.players}`;
  }
}
